import React, { useEffect, useState, useCallback, Suspense, useRef } from 'react';
import { useRouter } from 'next/compat/router';
import {
  usePlaidLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkOptions,
  PlaidLinkStableEvent,
  PlaidLinkOnEventMetadata,
  PlaidLinkOnLoad,
} from 'react-plaid-link';
import { useObserver } from 'mobx-react-lite';
import { useApolloClient, useQuery } from '@apollo/client';

import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import {
  DUTCHIE_PAY_QUERY_PARMAS_KEY,
  EnrollmentSteps,
  PlaidLinkCustomizationNames,
  PLAID_OAUTH_PATH,
  PLAID_STORAGE_KEY,
  Aggregator,
  IS_E2E,
  LOG_NAMESPACES,
} from 'src/payments/constants';
import {
  BankConnectionContexts,
  EVENT_ACTIONS,
  useDutchiePayAnalytics,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { useStores } from 'contexts/stores';
import completeAggregatorLink from 'src/payments/graphql/mutations/complete-aggretator-link.gql';
import meConsumer from 'shared/graphql/user/queries/me-consumer.gql';
import { StartAggregatorLinkV2Document } from 'src/gql/graphql';

import type { MeConsumerQuery } from 'src/gql/graphql';
import { parsePlaidMetaData } from 'src/payments/utils/parse-meta-data';
import { formatPaymentsError, getGraphQLReasonCode } from 'src/payments/hooks/use-payments-error/use-payments-error';
import { useIncentives } from 'src/payments/hooks/use-incentives/use-incentives';
import { useDutchiePay } from 'src/payments/hooks';
import useUI from 'hooks/use-ui';
import useViewport from 'hooks/use-viewport';
import useErnie from 'shared/hooks/use-ernie';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { ModalNames } from 'src/components/modals';
import { getLogger } from 'src/utils/datadog-logger';
import { PlaidInterimLoader } from './plaid-interim-loader';

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

const MockAggregatorWidget = React.lazy(() => import('src/payments/components/mock-aggregator-widget'));

export function AggregatorWidget({ relinkBankAccountEnabled }): JSX.Element {
  const apolloClient = useApolloClient();
  const {
    completeAggregatorLinkSucceeded,
    aggregatorLinkExited,
    completeAggregatorLinkStarted,
    accountChurnDetected,
  } = useDutchiePayEnrollment();
  const {
    trackPlaidOnEvent,
    trackEnrollmentStepSubmitted,
    trackEnrollmentStepViewed,
    trackPlaidOnLoad,
    trackPlaidOnSuccess,
    trackPlaidOnExit,
  } = useDutchiePayAnalytics();
  const hasTrackedImpressionRef = useRef(false);
  const { User, Modals } = useStores();
  const { paymentMethodId, shouldIncludePaymentMethodId, whiteLabelConfig } = useDutchiePay();
  const router = useRouter();
  const { asPath, query } = router ?? {};
  const showErnie = useErnie();
  const { embeddedEventDispatcher } = useUI();
  const viewport = useViewport();
  const { showPaymentBanner } = useIncentives();
  const { dispensary } = useDispensary();
  const { DEFAULT, INCENTIVES } = PlaidLinkCustomizationNames;

  const [redirectUrl, setRedirectUrl] = useState('');
  const [token, setToken] = useState<string | null>(null);

  const userFirstName = useObserver(() => User?.profile?.firstName);
  const userLastName = useObserver(() => User?.profile?.lastName);
  const userEmail = useObserver(() => User?.email);
  const userPhone = useObserver(() => User?.profile?.phone);

  const isOAuthRedirect = asPath?.includes(PLAID_OAUTH_PATH) ?? false;
  const queryPramas = localStorage.getItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);
  const { closeModal, activeModals } = Modals;
  const viewportHeight = viewport.height;

  const dispensaryName = dispensary?.name;
  const dispensaryId = dispensary?.id;

  const missingRequiredUserInfo = !userFirstName || !userLastName || !userEmail || !userPhone;
  const connectionMethod = BankConnectionContexts.BANK_CONNECTION_AGGREGATOR;

  const {
    data: startAggregatorLinkData,
    error: startAggregatorLinkError,
    loading: startAggregatorLinkLoading,
  } = useQuery(StartAggregatorLinkV2Document, {
    variables: {
      aggregator: Aggregator.PLAID,
      firstName: userFirstName,
      lastName: userLastName,
      paymentMethodId: shouldIncludePaymentMethodId ? paymentMethodId : '',
      email: userEmail,
      phone: userPhone,
      linkCustomizationName: showPaymentBanner ? INCENTIVES : DEFAULT,
    },
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- boolean OR, not a null-coalescing default
    skip: (relinkBankAccountEnabled && !paymentMethodId) || missingRequiredUserInfo,
    fetchPolicy: 'network-only',
  });

  if (startAggregatorLinkError) {
    const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(
      startAggregatorLinkError,
      whiteLabelConfig.brandedDutchiePayName
    );
    logger.error('AggregatorWidget: Error starting aggregator link', {
      error: startAggregatorLinkError,
      dispensaryId: dispensary?.id ?? 'Unknown dispensary',
      dispensaryName: dispensary?.name ?? 'Unknown dispensary',
      userId: User?.id ?? 'Unknown user',
      step: 'connectBank',
      linkCustomizationName: showPaymentBanner ? INCENTIVES : DEFAULT,
    });
    showErnie(errorMarkdown, ernieType, ernieTimeout);
    aggregatorLinkExited();
  }

  const persistLinkData = !token && !redirectUrl && !startAggregatorLinkLoading && startAggregatorLinkData;
  const plaidLinkToken = startAggregatorLinkData?.startAggregatorLinkV2.plaid?.linkToken ?? '';

  const clearPlaidData = (): void => {
    localStorage.removeItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);
    localStorage.removeItem(PLAID_STORAGE_KEY);
    setRedirectUrl('');
  };

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- async Plaid callback; void return type is a library type limitation
    async (publicToken, metadata) => {
      trackPlaidOnSuccess({ eventMetadata: metadata, connectionMethod });
      completeAggregatorLinkStarted();
      const formattedMetaData = parsePlaidMetaData({ metadata });

      // Default to the stale paymentMethodId as a fallback in case the meConsumer refetch fails.
      let resolvedPaymentMethodId = shouldIncludePaymentMethodId ? paymentMethodId : '';
      try {
        const { data: userData } = await apolloClient.query<MeConsumerQuery>({
          query: meConsumer,
          fetchPolicy: 'no-cache',
        });
        if (userData?.meConsumer) {
          User.setUser(userData.meConsumer);
        }
        const freshActiveBankAccount = User.payByBankBankAccounts?.find((acct) => acct?.status === 'active');
        resolvedPaymentMethodId =
          shouldIncludePaymentMethodId && freshActiveBankAccount ? freshActiveBankAccount.id : '';
      } catch (meConsumerErr) {
        logger.error('AggregatorWidget: Error fetching meConsumer, falling back to cached paymentMethodId', {
          error: meConsumerErr,
          dispensaryId,
          dispensaryName,
          userId: User?.id,
        });
      }

      try {
        const response = await apolloClient.mutate({
          mutation: completeAggregatorLink,
          variables: {
            aggregatorToken: publicToken,
            aggregator: Aggregator.PLAID,
            plaid: { ...formattedMetaData },
            paymentMethodId: resolvedPaymentMethodId,
            dispensaryName,
            dispensaryId,
          },
        });

        const pendingPaymentMethod = response.data.completeAggregatorLink.paymentMethods.filter(
          (method) => method.status === 'pending'
        );

        const bankDetails = {
          lastDigits: metadata.accounts[0].mask,
          bankName: metadata.institution?.name,
          id: pendingPaymentMethod[0].id,
          ownerName: User.fullName,
          accountName: metadata.accounts[0].name,
        };

        if (pendingPaymentMethod) {
          trackEnrollmentStepSubmitted({
            step: EnrollmentSteps.connectBank,
            action: EVENT_ACTIONS.SUCCEEDED,
            context: connectionMethod,
          });
          completeAggregatorLinkSucceeded(bankDetails);
        }

        if (isOAuthRedirect && queryPramas) {
          void router?.replace({
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
            query: { ...router?.query, ...JSON.parse(queryPramas) },
          });
        }
      } catch (err) {
        const reasonCode = getGraphQLReasonCode(err);

        if (reasonCode === 'E006') {
          logger.warn('AggregatorWidget: Plaid account churn detected (E006)', {
            error: err,
            dispensaryId,
            dispensaryName,
            userId: User?.id,
            step: 'connectBank',
            linkCustomizationName: showPaymentBanner ? INCENTIVES : DEFAULT,
            plaid: { ...formattedMetaData },
          });
          accountChurnDetected();
        } else {
          const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(
            err,
            whiteLabelConfig.brandedDutchiePayName
          );
          showErnie(errorMarkdown, ernieType, ernieTimeout);
          // close the terms modal if its open on error
          if (activeModals.has(ModalNames.termsOfService)) {
            void closeModal(ModalNames.termsOfService);
          }

          logger.error('AggregatorWidget: Error completing aggregator link', {
            error: err,
            dispensaryId,
            dispensaryName,
            userId: User?.id,
            step: 'connectBank',
            linkCustomizationName: showPaymentBanner ? INCENTIVES : DEFAULT,
            plaid: { ...formattedMetaData },
          });
          aggregatorLinkExited();
        }
      } finally {
        clearPlaidData();
      }
    },
    [
      completeAggregatorLinkStarted,
      apolloClient,
      shouldIncludePaymentMethodId,
      paymentMethodId,
      User,
      isOAuthRedirect,
      queryPramas,
      completeAggregatorLinkSucceeded,
      router,
      whiteLabelConfig.brandedDutchiePayName,
      showErnie,
      aggregatorLinkExited,
      accountChurnDetected,
      dispensaryName,
      dispensaryId,
      closeModal,
      activeModals,
      DEFAULT,
      INCENTIVES,
      showPaymentBanner,
      connectionMethod,
      trackPlaidOnSuccess,
      trackEnrollmentStepSubmitted,
    ]
  );

  const onEvent = useCallback<PlaidLinkOnEvent>(
    (eventName: PlaidLinkStableEvent, eventMetadata: PlaidLinkOnEventMetadata) => {
      trackPlaidOnEvent({ eventName, eventMetadata, connectionMethod });
    },
    [trackPlaidOnEvent, connectionMethod]
  );

  const onExit = useCallback<PlaidLinkOnExit>(
    (plaidError, eventMetadata) => {
      trackPlaidOnExit({ plaidError, eventMetadata, connectionMethod });
      clearPlaidData();
      aggregatorLinkExited();
    },
    [aggregatorLinkExited, trackPlaidOnExit, connectionMethod]
  );

  const onLoad = useCallback<PlaidLinkOnLoad>(() => {
    trackPlaidOnLoad({ connectionMethod, phonePassedToPlaid: Boolean(userPhone) });
  }, [trackPlaidOnLoad, connectionMethod, userPhone]);

  const config: PlaidLinkOptions = {
    token,
    onSuccess,
    onEvent,
    onExit,
    onLoad,
  };

  if (isOAuthRedirect) {
    config.receivedRedirectUri = redirectUrl;
  }

  useEffect(() => {
    if (persistLinkData) {
      setToken(plaidLinkToken);
      // save this in localstorage in case we have an oAuth redirect
      localStorage.setItem(PLAID_STORAGE_KEY, plaidLinkToken);
    }
  }, [persistLinkData, plaidLinkToken]);

  useEffect(() => {
    if (asPath?.includes(PLAID_OAUTH_PATH)) {
      setRedirectUrl(window.location.href);
      setToken(localStorage.getItem(PLAID_STORAGE_KEY));
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- asPath can be undefined
    if (asPath?.includes('?') && !asPath?.includes(PLAID_OAUTH_PATH)) {
      // save the query params in localstorage in case we use oAuth
      localStorage.setItem(DUTCHIE_PAY_QUERY_PARMAS_KEY, JSON.stringify(query));
    }
  }, [asPath, query]);

  useEffect(() => {
    if (!viewportHeight) {
      return;
    }
    embeddedEventDispatcher(`iframe:resize:override`, { height: viewportHeight });
    embeddedEventDispatcher(`iframe:scrollToTop`, { force: true });
  }, [embeddedEventDispatcher, viewportHeight]);

  const { open, ready } = usePlaidLink(config);
  useEffect(() => {
    if (IS_E2E) {
      return;
    }
    if (ready) {
      if (!hasTrackedImpressionRef.current) {
        trackEnrollmentStepViewed({
          step: EnrollmentSteps.connectBank,
          context: BankConnectionContexts.BANK_CONNECTION_AGGREGATOR,
        });
        hasTrackedImpressionRef.current = true;
      }
      open();
    }
  }, [open, ready, trackEnrollmentStepViewed]);

  // Show loader while were fetching token
  if (!IS_E2E && startAggregatorLinkLoading) {
    return <PlaidInterimLoader />;
  }

  // In E2E mode show the mock widget immediately — don't wait for usePlaidLink to
  // become ready (which requires loading the Plaid SDK from CDN and a valid user
  // profile). The mock widget bypasses the real Plaid flow entirely.
  if (IS_E2E) {
    return (
      <Suspense fallback={null}>
        <MockAggregatorWidget aggregator={Aggregator.PLAID} onSuccess={onSuccess} onExit={onExit} />
      </Suspense>
    );
  }

  return <></>;
}
